import React from 'react';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
import Box from '@mui/material/Box';
import Tab from '@mui/material/Tab';

import type { TabNavigationDelegate } from 'ui';
import { theme } from 'ui/theme';
import { Spinner } from '../../atoms';
import type { PageTab } from '../TabbedPage/tabbed-page.template';

interface Props extends Record<string, unknown> {
  tabs: PageTab[];
  onTabChange?: (currentTab: string) => void;
  selectedIndexOnMount?: number;
  loading?: boolean;
  // Lets a SaveChangesModal block sub-tab navigation while there are unsaved changes. GS-18859
  tabNavigationDelegate?: TabNavigationDelegate;
}

export const TabbedSecondaryPage: React.FC<Props> = (props) => {
  const [value, setValue] = React.useState('1');

  React.useEffect(() => {
    // Allow persisting of selected secondary tab when parent tabs switch.
    if (props.selectedIndexOnMount) {
      const index =
        props.selectedIndexOnMount > -1 && props.selectedIndexOnMount < props.tabs.length
          ? props.selectedIndexOnMount
          : 0;
      setValue(`${index + 1}`);
    }
  }, []);

  const { onTabChange, tabs, tabNavigationDelegate: delegate } = props;

  const applyTabChange = React.useCallback(
    (index: number) => {
      setValue(`${index + 1}`);
      onTabChange ? onTabChange(tabs[index].id) : null;
    },
    [onTabChange, tabs]
  );

  // The delegate switches tabs through us, so it can withhold the change while a save prompt is up.
  React.useEffect(() => {
    delegate?.setChangeTabFunction(applyTabChange);
  }, [delegate, applyTabChange]);

  const handleChange = (event: React.SyntheticEvent, newValue: string) => {
    const index = parseInt(newValue) - 1;
    delegate ? delegate.changeTab(index) : applyTabChange(index);
  };

  if (props.loading) return <Spinner />;
  return (
    <Box sx={{ width: '100%', typography: 'body1' }}>
      <TabContext value={value}>
        {/* No left padding - the panel below renders flush, so an inset would offset the tab. */}
        <Box sx={{ borderBottom: 0, borderColor: 'divider' }}>
          <TabList
            onChange={handleChange}
            sx={{
              '& .MuiTabs-scroller': {
                '& .MuiTabs-indicator': {
                  backgroundColor: `${theme.palette.divider}`,
                  height: 0
                }
              }
            }}
          >
            {props.tabs.length > 0 &&
              props.tabs.map((tab, index) => (
                <Tab
                  key={tab.id}
                  label={tab.title}
                  value={`${index + 1}`}
                  sx={{
                    padding: '11px 20px 11px 20px',
                    color: `${theme.palette.text.primary}`,
                    fontSize: '14px',
                    lineHeight: '20px',
                    fontWeight: '400',
                    textTransform: 'capitalize',
                    '&:hover': {
                      color: theme.palette.primary.main
                    },
                    '&.Mui-selected': {
                      border: `1px solid ${theme.palette.divider}`,
                      color: `${theme.palette.text.primary}`,
                      borderBottom: '0px'
                    }
                  }}
                />
              ))}
          </TabList>
        </Box>
        {props.tabs.length > 0 &&
          props.tabs.map((tab, index) => {
            // We must modify the React.FC to be a JSX.Element for TabPanel to accept it.
            const Component = tab.component;

            return (
              <TabPanel key={tab.id} style={{ padding: 0 }} value={`${index + 1}`}>
                {Component ? <Component /> : null}
              </TabPanel>
            );
          })}
      </TabContext>
    </Box>
  );
};

export default TabbedSecondaryPage;
